/**
 * PagesPopup — BitDrop Sidebar Info & Pages navigation popup molecule.
 * 
 * Features:
 * - Click-outside dismissal (via window listener + touch/mouse backdrop)
 * - Strict monochrome styling with theme standard tokens
 * - Humanizer compliance (zero em dashes, SVG icons)
 */

import React, { useEffect, useRef } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, TouchableWithoutFeedback } from 'react-native';
import { useTheme } from '../hooks/useTheme';
import { Radius, FontSize, FontWeight, Spacing, Layout } from '../theme/tokens';
import { Icon } from '../atoms/Icon';
import { INFO_PAGES_LINKS } from '../constants/navigation';

export interface PagesPopupProps {
  visible: boolean;
  onClose: () => void;
  triggerRef?: React.RefObject<any>;
}

export const PagesPopup: React.FC<PagesPopupProps> = ({
  visible,
  onClose,
  triggerRef,
}) => {
  const { colors } = useTheme();
  const popupRef = useRef<any>(null);

  useEffect(() => {
    if (!visible) return;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!target) return;

      const popupEl = popupRef.current;
      const triggerEl = triggerRef?.current;

      if (
        popupEl &&
        !popupEl.contains?.(target) &&
        (!triggerEl || !triggerEl.contains?.(target))
      ) {
        onClose();
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('mousedown', handlePointerDown, true);
      window.addEventListener('touchstart', handlePointerDown, true);
      return () => {
        window.removeEventListener('mousedown', handlePointerDown, true);
        window.removeEventListener('touchstart', handlePointerDown, true);
      };
    }
  }, [visible, onClose, triggerRef]);

  if (!visible) return null;

  return (
    <>
      {/* Fullscreen transparent backdrop for click-outside dismissal */}
      <TouchableWithoutFeedback onPress={onClose}>
        <View style={styles.backdrop} />
      </TouchableWithoutFeedback>

      {/* Info popup card */}
      <View
        ref={popupRef}
        style={[
          styles.popup,
          {
            backgroundColor: colors.surface,
            borderColor:     colors.border,
          },
        ]}
        onTouchStart={(e) => e.stopPropagation()}
      >
        <Text style={[styles.title, { color: colors.text }]}>BitDrop Pages</Text>
        <Text style={[styles.subtitle, { color: colors.subtext }]}>
          100% peer-to-peer file sharing with zero servers involved.
        </Text>

        <View style={[styles.divider, { backgroundColor: colors.border }]} />

        {INFO_PAGES_LINKS.map(({ icon, label, href }) => (
          <TouchableOpacity
            key={label}
            style={styles.row}
            onPress={() => {
              if (typeof window !== 'undefined') {
                window.location.href = href;
              }
            }}
            activeOpacity={0.75}
          >
            <Icon name={icon} size={14} color={colors.brand} />
            <Text style={[styles.rowLabel, { color: colors.text }]}>{label}</Text>
          </TouchableOpacity>
        ))}
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    position:        'fixed' as any,
    top:             0,
    left:            0,
    right:           0,
    bottom:          0,
    zIndex:          90,
    backgroundColor: 'transparent',
    pointerEvents:   'auto' as any,
  } as any,
  popup: {
    position:      'fixed' as any,
    bottom:        80,
    left:          Layout.sidebarWidth + 16,
    width:         220,
    borderWidth:   1,
    borderRadius:  Radius.lg,
    padding:       Spacing.md - 1,
    zIndex:        99,
    pointerEvents: 'auto' as any,
  } as any,
  title: {
    fontSize:     FontSize.md,
    fontWeight:   FontWeight.bold,
    marginBottom: 4,
  },
  subtitle: {
    fontSize:     FontSize.xs,
    lineHeight:   18,
    marginBottom: Spacing.sm,
  },
  divider: {
    height:       1,
    marginBottom: Spacing.sm,
  },
  row: {
    flexDirection:   'row',
    alignItems:      'center',
    gap:             8,
    paddingVertical: 5,
  } as any,
  rowLabel: {
    fontSize:   FontSize.sm,
    fontWeight: FontWeight.medium,
  },
});
